<script lang="ts">
import type { IconName } from './icons'

export type MenuEntry =
  | { label: string; icon?: IconName; onSelect: () => void; danger?: boolean; disabled?: boolean
      /** A short text at the end of the line: a limit, a count. */
      hint?: string }
  | { separator: true }

/** One option of a menu that picks a value (a language): announced as checked or not. */
export interface MenuChoice { value: string; label: string }
</script>

<script setup lang="ts">
/**
 * Dropdown menu. With `items`, a list of actions. With `choices` and
 * `modelValue`, a radio group (role menuitemradio, aria-checked): the current
 * value is said to assistive technology, not only drawn as a check.
 */
import { DropdownMenuContent, DropdownMenuItem, DropdownMenuItemIndicator, DropdownMenuPortal,
  DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuRoot, DropdownMenuSeparator,
  DropdownMenuTrigger } from 'reka-ui'
import { cn } from '@/lib/cn'
import Icon from './Icon.vue'

withDefaults(defineProps<{
  items?: MenuEntry[]; choices?: MenuChoice[]; modelValue?: string; width?: number; align?: 'start' | 'end'
}>(), { items: () => [], width: 200, align: 'end' })
const emit = defineEmits<{ 'update:modelValue': [v: string] }>()
const ITEM = 'flex cursor-pointer select-none items-center gap-2 rounded-[5px] px-2 py-1.5 text-[13px] outline-none transition-colors duration-fast ' +
  'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[highlighted]:bg-muted'
</script>

<template>
  <DropdownMenuRoot>
    <DropdownMenuTrigger as-child><slot /></DropdownMenuTrigger>
    <DropdownMenuPortal>
      <DropdownMenuContent :align="align" :side-offset="4" :collision-padding="8" :style="{ width: `${width}px` }"
                           class="z-dropdown rounded-card border border-border bg-popover p-1 text-popover-foreground shadow-pop
                                  data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[.97] data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-[.97]">
        <DropdownMenuRadioGroup v-if="choices" :model-value="modelValue"
                                @update:model-value="(v) => emit('update:modelValue', String(v))">
          <DropdownMenuRadioItem v-for="c in choices" :key="c.value" :value="c.value" :class="ITEM">
            <span class="grid h-[15px] w-[15px] flex-none place-items-center">
              <DropdownMenuItemIndicator><Icon name="check" :size="15" /></DropdownMenuItemIndicator>
            </span>
            <span class="min-w-0 flex-1 truncate">{{ c.label }}</span>
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <template v-for="(e, i) in items" :key="i">
          <DropdownMenuSeparator v-if="'separator' in e" class="my-1 h-px bg-border" />
          <DropdownMenuItem v-else :disabled="e.disabled" @select="e.onSelect" :class="cn(ITEM, e.danger && 'text-danger')">
            <Icon v-if="e.icon" :name="e.icon" :size="15" />
            <span class="min-w-0 flex-1 truncate">{{ e.label }}</span>
            <span v-if="e.hint" class="flex-none text-[11.5px] text-muted-foreground">{{ e.hint }}</span>
          </DropdownMenuItem>
        </template>
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>
